import type { Exchange, ExchangeId } from '../types/exchange';
import type { TradingPair } from '../types/pair';
import type { OHLCV, Timeframe } from '../types/ohlcv';
import type { DetectedPattern } from '../types/pattern';
import type { Ticker24h } from '../types/ticker';

export interface OHLCVRequest {
  exchangeId: ExchangeId;
  /** Canonical pair symbol, always `BASE/QUOTE` (e.g. "BTC/USD"). */
  pair: string;
  timeframe: Timeframe;
  /** Window start, Unix ms (inclusive). */
  from: number;
  /** Window end, Unix ms (inclusive). */
  to: number;
}

/** A live stream subscription; call to stop it. */
export type Unsubscribe = () => void;

export type StreamStatus = 'connecting' | 'open' | 'closed' | 'error';

/**
 * Where HopCharts gets its market data. Implement `getOHLCV` and the chart
 * works; the optional methods light up the exchange/pair pickers, the ticker,
 * live streams and the AI overlay. A provider is plain data access — it must
 * not touch the DOM or React.
 *
 * Contract for `getOHLCV`: resolve with candles oldest-first, timestamps in
 * Unix ms, at most one candle per timestamp, covering `[from, to]` as far as
 * the venue has data. Return `[]` (not an error) when the range is empty.
 *
 * Contract for `subscribeOHLCV`: call `onCandle` with the forming candle (and
 * the just-closed one when a new bucket starts) of exactly the requested
 * timeframe, timestamps on the bucket open. The chart falls back to polling
 * `getOHLCV` when the method is missing.
 */
export interface DataProvider {
  /** Stable identifier; it is part of every react-query cache key. */
  id: string;
  getOHLCV(request: OHLCVRequest): Promise<OHLCV[]>;
  getExchanges?(): Promise<Exchange[]>;
  getPairs?(exchangeId: ExchangeId): Promise<TradingPair[]>;
  getTicker?(exchangeId: ExchangeId, pair: string): Promise<{ last: number }>;
  /**
   * 24h statistics for every market on the exchange (screeners, watchlists). With `pairs`, an exchange that
   * can fetch a few markets on their own does so; the others still return every market, so filter the result.
   */
  getTickers?(exchangeId: ExchangeId, pairs?: string[]): Promise<Ticker24h[]>;
  subscribeOHLCV?(
    request: Omit<OHLCVRequest, 'from' | 'to'>,
    onCandle: (candle: OHLCV) => void,
    onStatus?: (status: StreamStatus) => void,
  ): Unsubscribe;
  subscribeTickers?(
    exchangeId: ExchangeId,
    pairs: string[],
    onTicker: (ticker: Ticker24h) => void,
  ): Unsubscribe;
  getAIPatterns?(request: Omit<OHLCVRequest, 'from' | 'to'>): Promise<DetectedPattern[]>;
}

export class ProviderError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ProviderError';
  }
}
